import moment from "moment-timezone";
import { OnlineOrdersConfigs, SurchargeConfig } from "../store/kitchen/kitchen.types";
import { getKitchenTimezone } from "./timezone";

export type ActiveSurcharge = {
  enabled: boolean;
  name: "Public Holiday" | "Sunday" | "Saturday" | null;
  value: number;
};

const defaultSurchargeConfig: SurchargeConfig = {
  enabled: false,
  value: 0,
};

export const getSurchargeConfigWithDefaults = (
  surchargeConfig?: SurchargeConfig
): SurchargeConfig => {
  if (!surchargeConfig) {
    return defaultSurchargeConfig;
  }

  return {
    enabled: Boolean(surchargeConfig.enabled),
    value: Number(surchargeConfig.value) || 0,
  };
};

const normalizeSurchargeRate = (value: number): number => {
  const parsedValue = Number(value) || 0;
  // Supports both decimal rates (0.1) and percent values (10).
  return parsedValue > 1 ? parsedValue / 100 : parsedValue;
};

export const getActiveSurcharge = (
  onlineOrdersConfig?: OnlineOrdersConfigs | null,
  timezone?: string,
  now: Date = new Date()
): ActiveSurcharge => {
  // Weekday must reflect the venue's local day, not the customer's browser.
  const weekday = moment.tz(now, getKitchenTimezone(timezone)).day();
  const surchargeConfigs = onlineOrdersConfig?.surchargeConfigs;
  const publicHolidaySurcharge = getSurchargeConfigWithDefaults(
    onlineOrdersConfig?.publicHolidaySurcharge ||
      surchargeConfigs?.publicHolidaySurcharge
  );
  const sundaySurcharge = getSurchargeConfigWithDefaults(
    onlineOrdersConfig?.sundaySurcharge || surchargeConfigs?.sundaySurcharge
  );
  const saturdaySurcharge = getSurchargeConfigWithDefaults(
    onlineOrdersConfig?.saturdaySurcharge || surchargeConfigs?.saturdaySurcharge
  );

  if (publicHolidaySurcharge.enabled) {
    return {
      enabled: true,
      name: "Public Holiday",
      value: normalizeSurchargeRate(publicHolidaySurcharge.value),
    };
  }

  if (sundaySurcharge.enabled && weekday === 0) {
    return {
      enabled: true,
      name: "Sunday",
      value: normalizeSurchargeRate(sundaySurcharge.value),
    };
  }

  if (saturdaySurcharge.enabled && weekday === 6) {
    return {
      enabled: true,
      name: "Saturday",
      value: normalizeSurchargeRate(saturdaySurcharge.value),
    };
  }

  return {
    enabled: false,
    name: null,
    value: 0,
  };
};

export const calculateHolidaySurcharge = (
  cartSubtotal: number,
  activeSurcharge: ActiveSurcharge
): number => {
  if (!activeSurcharge.enabled) {
    return 0;
  }

  return (Number(cartSubtotal) || 0) * (Number(activeSurcharge.value) || 0);
};
